import React from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  resetErrorAction,
  setSubscriptionListAction,
} from 'root-redux/actions/common'

import { useActiveCohortIdentifier } from 'hooks/useActiveCohortIdentifier'
import { useCohortToUse } from 'hooks/useCohortToUse'
import { useIsPrimerEnabled } from 'hooks/useIsPrimerEnabled'
import { usePurchaseCancelOfferRoute } from 'hooks/usePurchaseCancelOfferRoute'

import { createProductId } from 'helpers/createProductId'

import { usePurchaseStore } from 'modules/purchase/hooks'
import {
  setIsFirstPaymentRetryPassedAction,
  setSelectedSubscriptionAction,
} from 'modules/purchase/redux/actions/common'

import { eventLogger } from 'services/eventLogger.service'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { goTo } from 'browser-history'
import { ScreenName } from 'root-constants'

import { Checkout } from '../Checkout'
import {
  CheckoutSeparateMethods2,
  CheckoutSeparateMethodsMix,
} from '../CheckoutSeparateMethods'
import { CANCEL_OFFER_COHORTS, SEPARATED_CHECKOUT_COHORTS } from './constants'

type TCheckoutList = {
  setIsCheckoutShown: (isCheckoutShown: boolean) => void
  isCancelOffer: boolean
}
const CheckoutList: React.FC<TCheckoutList> = ({
  setIsCheckoutShown,
  isCancelOffer,
}: TCheckoutList) => {
  const dispatch = useDispatch()

  const { pathname, search } = useLocation()
  const cohortToUse = useCohortToUse()

  const isPrimerFormActive = useIsPrimerEnabled()

  const { isCoralThemeCohort } = useActiveCohortIdentifier()

  const { cohort, hasCancelOffer, periodName, periodQuantity, price } =
    usePurchaseStore()

  const cancelOfferRoutePath = usePurchaseCancelOfferRoute()

  const isSeparatedCheckout = SEPARATED_CHECKOUT_COHORTS.includes(cohortToUse)

  const productId = createProductId({ periodName, periodQuantity, price })

  const cancelOfferCohort =
    hasCancelOffer && CANCEL_OFFER_COHORTS.includes(cohortToUse)

  const handleCloseCheckout = () => {
    dispatch(resetErrorAction())

    googleAnalyticsLogger.logPageView(`${pathname}/${cohort}`)
    eventLogger.logPurchaseScreenClosed({
      productId,
      screenName: isCancelOffer
        ? ScreenName.CANCEL_OFFER_LONG
        : ScreenName.PURCHASE,
    })

    if (isPrimerFormActive) {
      dispatch(setIsFirstPaymentRetryPassedAction(false))
    }

    if (cancelOfferCohort && !isCancelOffer) {
      dispatch(setSubscriptionListAction([]))
      dispatch(setSelectedSubscriptionAction(null))
      goTo({ pathname: cancelOfferRoutePath, search })
    }
    setIsCheckoutShown(false)
  }

  if (isCoralThemeCohort) {
    return (
      <CheckoutSeparateMethods2 handleCloseCheckout={handleCloseCheckout} />
    )
  }

  if (isSeparatedCheckout) {
    return (
      <CheckoutSeparateMethodsMix
        handleCloseCheckout={handleCloseCheckout}
        isPrimerFormActive={isPrimerFormActive}
      />
    )
  }

  return (
    <Checkout
      handleCloseCheckout={handleCloseCheckout}
      hasTotalAmount={false}
    />
  )
}

export default CheckoutList
